/**
 * 错误边界组件
 *
 * 使用 antd Result 组件，支持 antd-style。
 */

import { Button, Result, theme } from 'antd';
import React from 'react';

/**
 * ErrorBoundary 组件属性
 */
export interface ErrorBoundaryProps {
  /** 子组件 */
  children: React.ReactNode;
  /** 自定义 fallback UI */
  fallback?:
    | React.ReactNode
    | ((error: Error, reset: () => void) => React.ReactNode);
  /** 错误回调 (用于上报) */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  /** 重置回调 */
  onReset?: () => void;
  /** 自定义标题 */
  title?: string;
  /** 自定义描述 */
  description?: string;
  /**
   * 是否显示错误详情
   * - true: 始终显示
   * - false: 不显示
   * - 'auto': 默认不显示（可通过 showDetails 显式开启）
   */
  showDetails?: boolean | 'auto';
  /** 刷新页面按钮文本，默认 '刷新页面' */
  reloadText?: string;
  /** 重试按钮文本，默认 '重试' */
  retryText?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

/**
 * 错误边界组件
 *
 * 捕获子组件渲染错误，显示 fallback UI 并支持错误恢复。
 *
 * @example
 * ```tsx
 * // 基础用法
 * <ErrorBoundary>
 *   <MyComponent />
 * </ErrorBoundary>
 *
 * // 自定义 fallback
 * <ErrorBoundary
 *   fallback={(error, reset) => (
 *     <div>
 *       <p>出错了: {error.message}</p>
 *       <button onClick={reset}>重试</button>
 *     </div>
 *   )}
 *   onError={(error) => reportError(error)}
 * >
 *   <MyComponent />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.setState({ errorInfo });
    this.props.onError?.(error, errorInfo);
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    this.props.onReset?.();
  };

  handleReload = (): void => {
    window.location.reload();
  };

  shouldShowDetails(): boolean {
    const { showDetails = 'auto' } = this.props;
    if (showDetails === 'auto') {
      return false;
    }
    return showDetails;
  }

  render(): React.ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { error, errorInfo } = this.state;
    const { fallback, title, description, reloadText, retryText } = this.props;

    // 自定义 fallback
    if (fallback !== undefined && fallback !== null) {
      if (typeof fallback === 'function' && error) {
        return fallback(error, this.handleReset) as React.ReactNode;
      }
      if (typeof fallback !== 'function') {
        return fallback as React.ReactNode;
      }
    }

    // 默认 fallback UI
    return (
      <Result
        status="error"
        title={title || '页面错误'}
        subTitle={description || '抱歉，页面渲染时发生了错误'}
        extra={[
          <Button key="reload" type="primary" onClick={this.handleReload}>
            {reloadText ?? '刷新页面'}
          </Button>,
          <Button key="reset" onClick={this.handleReset}>
            {retryText ?? '重试'}
          </Button>,
        ]}
      >
        {this.shouldShowDetails() && error && (
          <ErrorDetails
            error={error}
            componentStack={errorInfo?.componentStack ?? undefined}
          />
        )}
      </Result>
    );
  }
}

/**
 * 错误详情展示（函数组件，可使用 theme token）
 */
function ErrorDetails({
  error,
  componentStack,
}: {
  error: Error;
  componentStack?: string;
}) {
  const { token } = theme.useToken();

  return (
    <div
      style={{
        textAlign: 'left',
        padding: token.padding,
        background: token.colorBgLayout,
        borderRadius: token.borderRadiusSM,
        marginTop: token.marginMD,
        fontSize: token.fontSizeSM,
        fontFamily: 'monospace',
        maxHeight: 300,
        overflow: 'auto',
      }}
    >
      <div style={{ marginBottom: token.marginXS, fontWeight: 'bold' }}>
        {error.message}
      </div>
      {componentStack && (
        <pre
          style={{
            margin: 0,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
            color: token.colorTextSecondary,
          }}
        >
          {componentStack}
        </pre>
      )}
    </div>
  );
}
